import React, { useMemo } from 'react';
import type { PriceChangeSubmission } from '@app/model/pricing-automation/PriceChangeSubmission';
import { PriceChangeTooltipSection, priceChangeMarkerSeries, syncPriceChangeMarkerColor, tooltipPointIndex } from './PriceChangeMarkers';
import MarketStateDailySnapshotWithComparisonOutput
	from '@app/model/market_state/output/MarketStateDailySnapshotWithComparisonOutput';
import Highcharts from 'highcharts';
import 'highcharts/highcharts-more';

import Utilities from '@util/Utilities';
import moment from 'moment/moment';
import ReactDOMServer from 'react-dom/server';
import Message from '@app/component/message/Message';
import HighchartsReact from 'highcharts-react-official';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import { MarketStateStatDateSnapshot } from '@app/model/market_state/MarketStateStatDateSnapshot';
import {
	useMarketStateFilterInputTools
} from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import GraphUtilities from '@app/site/secured/rateshopper/rates/dashboard/GraphUtilities';


const MarketSateDayPriceEvolutionMedianGraph: React.FunctionComponent<{ data: MarketStateStatDateSnapshot[]; priceChanges: PriceChangeSubmission[] }>
	= ({ data, priceChanges }) => {

	const { companyHotel } = useSelectedCompanyHotel();

	const { filter } = useMarketStateFilterInputTools();

	Highcharts.SVGRenderer.prototype.symbols.cross = (x: number, y: number, w: number, h: number) => {
		return ['M', x, y, 'L', x + w, y + h, 'M', x + w, y, 'L', x, y + h, 'z'];
	};

	// Build the options only when the inputs change. A new options object makes HighchartsReact call
	// chart.update(), which resets the series that the user turned on or off in the legend.
	const options = useMemo(() => {
		const pointDates = data.map(day => day.latestUpdateDate);

		// @ts-ignore
		const marketStates = data!.map(d => new MarketStateDailySnapshotWithComparisonOutput({
			current: d,
			previous: undefined,
			statDate: d.statDate,
		}));

		const hotelLines = GraphUtilities.sortAllSeriesByAveragePrice(companyHotel, marketStates, true, true, true,
			filter.competitorTagIds, true);
		const markers = priceChangeMarkerSeries(pointDates, GraphUtilities.findMainHotelData(companyHotel, marketStates), priceChanges);

		// Check if availability data exists

		return {
			chart: {
				type: 'line',
				height: '800',
				events: { render: function(this: any) { syncPriceChangeMarkerColor(this); } },
			},
			title: {
				text: '',
			},
			plotOptions: { series: { states: { inactive: { enabled: false } } } },
			xAxis: {
				categories: marketStates.map((ms, index) => {
					const date = Utilities.formatDateString(ms.current.latestUpdateDate!);
					const now = moment().format('DD/MM/yyyy');
					return now == date ? 'Today' : date;
				}),
				alternateGridColor: '#f3f4f6',
				crosshair: true,
			},
			yAxis: {
				title: {
					text: 'Price',
				},
			},
			tooltip: {
				shared: true,
				useHTML: true,
				// @ts-ignore
				formatter: function() {
					// One tooltip for the day: the line and the price-change dot both show it.
					// @ts-ignore
					const chart = (this.points?.[0] ?? this).series.chart;
					const xValue = tooltipPointIndex(this);

					// Get all series data for this x-value, including hidden ones
					// @ts-ignore
					const allPoints = [];
					// @ts-ignore
					chart.series.forEach(series => {
						if (series.options.id === 'price-changes') return;
						const point = series.data[xValue];
						if (point && point.y !== null && point.y !== undefined) {
							allPoints.push({
								series: series,
								point: point,
								color: series.color,
							});
						}
					});

					// Sort points: general stats first, then hotel prices
					// @ts-ignore
					const sortedPoints = allPoints.sort((a, b) => {
						// @ts-ignore
						const getSortOrder = (seriesName) => {
							// General statistics first
							if (seriesName === 'Total Availability') return 1;
							if (seriesName === 'Sold Out Hotels') return 2;
							if (seriesName === 'Competitors Price Range') return 3;
							if (seriesName === 'Competitors ' + companyHotel.comparisonLabel()) return 4;

							// Main hotel next
							if (a.series.options.name === companyHotel.mainHotel.competitors[0].hotel.name) return 5;

							// Individual competitors last
							return 6;
						};

						return getSortOrder(a.series.name) - getSortOrder(b.series.name);
					});

					return ReactDOMServer.renderToString(<div>
						{formatTooltip(sortedPoints, chart.xAxis[0].categories[xValue])}
						<PriceChangeTooltipSection days={markers.days} pointIndex={xValue} />
					</div>);
				},
				valueDecimals: 2,
			},
			series: [
				GraphUtilities.competitorsPriceRangeSeries(marketStates),
				GraphUtilities.competitorsMedianLineSeries(marketStates, companyHotel),
				GraphUtilities.soldOutHotelsSeries(marketStates),
				GraphUtilities.availabilitySeries(marketStates),
				...hotelLines,
				...(markers.series ? [markers.series] : []),
			],
		};
	}, [data, priceChanges, companyHotel, filter]);

	function formatTooltip(points: any[], category: string) {
		return <table className="min-w-full divide-y divide-gray-300 text-gray-500">
			<thead>
			<tr>
				<th scope="col"
						className="py-2 px-3 text-left text-xs font-semibold text-yellow-900 sm:pl-0" colSpan={3}>
					{category}
				</th>
			</tr>
			</thead>
			<tbody className="divide-y divide-gray-200 bg-white">
			{points.map((point, idx) => {
				// Handle different data types for line charts vs bar charts
				let displayValue;
				if (point.series.name === 'Competitors Price Range') {
					// Handle arearange series - show min-max range
					const low = point.point.low;
					const high = point.point.high;
					displayValue = low && high ? `${Utilities.formatDouble(low, 0)} - ${Utilities.formatDouble(high, 0)}` : 'N/A';
				} else {
					displayValue = point.point.displayValue ||
						(point.series.name === 'Sold Out Hotels' ? `${point.point.y}/${point.point.total}` :
							point.point.val ? Utilities.formatDouble(point.point.val, 0) : 'N/A');
				}

				return <tr key={idx}>
					<td className="whitespace-nowrap px-2 py-2 text-xs"
							style={{ color: point.series.color }}>{point.series.name}</td>
					<td className="whitespace-nowrap px-2 py-2 text-xs" style={{ textAlign: 'right' }}>
						<b>{displayValue}</b>
					</td>
				</tr>;
			})}
			</tbody>
		</table>;
	}

	const highcharts = Highcharts;

	return <div style={{ position: 'relative' }}>
		{GraphUtilities.isFilterComputable(filter) &&
			<Message extraClassName="mb-4" state="info">
				The graph is calculated using Booking Web public rates</Message>}
		<HighchartsReact
			highcharts={highcharts}
			options={options}
		/>
	</div>;
};

export default MarketSateDayPriceEvolutionMedianGraph;
